<template>
  <div class="knowledge-page">
    <section class="knowledge-hero reveal">
      <div class="knowledge-hero__media">
        <img :src="page.heroImage" alt="">
      </div>
      <div class="knowledge-hero__overlay"></div>
      <div class="container knowledge-hero__content">
        <p class="knowledge-hero__eyebrow">{{ page.eyebrow }}</p>
        <h1>{{ page.title }}</h1>
      </div>
    </section>

    <section class="knowledge-news reveal">
      <div class="knowledge-news__wrap">
        <h2 class="knowledge-news__title">{{ page.sectionTitle }}</h2>
        <div class="knowledge-news__list">
          <router-link
            v-for="item in paginatedArticles"
            :key="item.slug"
            :to="'/news/' + item.slug"
            class="knowledge-news__item"
          >
            <div class="knowledge-news__image">
              <img :src="item.image" :alt="item.title" loading="lazy">
            </div>
            <div class="knowledge-news__body">
              <h4>{{ item.title }}</h4>
              <p class="knowledge-news__date">{{ item.dateLabel }}</p>
              <p class="knowledge-news__excerpt">{{ item.excerpt }}</p>
            </div>
          </router-link>
        </div>

        <nav v-if="totalPages > 1" class="knowledge-pagination" aria-label="News pages">
          <button
            type="button"
            class="knowledge-pagination__btn"
            :disabled="currentPage <= 1"
            :aria-label="$t('common.previousPage')"
            @click="goToPage(currentPage - 1)"
          >
            ‹
          </button>
          <button
            v-for="pageNumber in totalPages"
            :key="pageNumber"
            type="button"
            class="knowledge-pagination__btn"
            :class="{ 'is-active': pageNumber === currentPage }"
            @click="goToPage(pageNumber)"
          >
            {{ pageNumber }}
          </button>
          <button
            type="button"
            class="knowledge-pagination__btn"
            :disabled="currentPage >= totalPages"
            :aria-label="$t('common.nextPage')"
            @click="goToPage(currentPage + 1)"
          >
            ›
          </button>
        </nav>
      </div>
    </section>

    <section v-if="videoItems.length" class="knowledge-videos reveal">
      <div class="knowledge-videos__wrap">
        <div class="knowledge-videos__grid">
          <button
            v-for="(item, index) in videoItems"
            :key="item.video"
            type="button"
            class="knowledge-videos__item"
            :class="{ 'knowledge-videos__item--featured': item.featured }"
            @click="openVideo(index)"
          >
            <img :src="item.poster" :alt="item.alt" loading="lazy">
            <span class="knowledge-videos__play" aria-hidden="true"></span>
          </button>
        </div>
      </div>
    </section>

    <transition name="fade">
      <div v-if="videoOpen" class="knowledge-video-modal" @click.self="closeVideo">
        <button type="button" class="knowledge-video-modal__close" :aria-label="$t('common.close')" @click="closeVideo">×</button>
        <button type="button" class="knowledge-video-modal__nav knowledge-video-modal__nav--prev" @click="stepVideo(-1)">‹</button>
        <video
          ref="modalVideo"
          class="knowledge-video-modal__player"
          controls
          playsinline
          :poster="activeVideo.poster"
          :src="activeVideo.video"
        ></video>
        <button type="button" class="knowledge-video-modal__nav knowledge-video-modal__nav--next" @click="stepVideo(1)">›</button>
      </div>
    </transition>
  </div>
</template>

<script>
import site from '../mock/strongova'
import { fetchPublicArticles, fetchPublicPageSections, normalizeArticle, resolveAssetUrl } from '../api/public'

export default {
  name: 'KnowledgePage',
  data () {
    return {
      page: site.knowledgePage,
      videoOpen: false,
      videoIndex: 0
    }
  },
  created () {
    Promise.all([
      fetchPublicArticles(),
      fetchPublicPageSections({ pageKey: 'knowledge' })
    ])
      .then(([articles, sections]) => {
        const page = { ...site.knowledgePage }

        if (Array.isArray(articles) && articles.length) {
          page.articles = articles.map(item => normalizeArticle(item, site.knowledgePage.heroImage))
        }

        this.applyPageSections(page, sections)
        this.page = page
      })
      .catch(() => {
        this.page = site.knowledgePage
      })
  },
  computed: {
    activeVideo () {
      return this.videoItems[this.videoIndex] || this.videoItems[0] || {}
    },
    videoItems () {
      return Array.isArray(this.page.videos) ? this.page.videos.filter(item => item.poster && item.video) : []
    },
    currentPage () {
      const page = parseInt(this.$route.query.page, 10)
      return page >= 1 && page <= this.totalPages ? page : 1
    },
    totalPages () {
      return Math.ceil(this.page.articles.length / this.page.pageSize)
    },
    paginatedArticles () {
      const start = (this.currentPage - 1) * this.page.pageSize
      return this.page.articles.slice(start, start + this.page.pageSize)
    }
  },
  mounted () {
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onKeydown)
    document.body.style.overflow = ''
  },
  methods: {
    applyPageSections (page, sections) {
      if (!Array.isArray(sections)) return
      const hero = sections.find(item => item.sectionKey === 'hero')
      const videos = sections.find(item => item.sectionKey === 'video')

      if (hero) {
        page.title = hero.title || page.title
        page.eyebrow = hero.description || page.eyebrow
        page.heroImage = resolveAssetUrl(hero.image, page.heroImage)
      }

      if (videos && Array.isArray(videos.imagesJson) && videos.imagesJson.length) {
        page.videos = videos.imagesJson
          .map((item, index) => {
            const fallback = page.videos[index] || page.videos[0] || {}
            return {
              poster: resolveAssetUrl(item.image, fallback.poster),
              video: fallback.video,
              alt: item.title || fallback.alt || 'STRONGOVA flooring video',
              featured: Boolean(fallback.featured)
            }
          })
          .filter(item => item.poster && item.video)
      }
    },
    goToPage (pageNumber) {
      const query = pageNumber > 1 ? { page: String(pageNumber) } : {}
      this.$router.push({ path: '/knowledge', query })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    openVideo (index) {
      this.videoIndex = index
      this.videoOpen = true
      document.body.style.overflow = 'hidden'
      this.$nextTick(() => {
        const player = this.$refs.modalVideo
        if (player) {
          player.currentTime = 0
          player.play().catch(() => {})
        }
      })
    },
    closeVideo () {
      const player = this.$refs.modalVideo
      if (player) {
        player.pause()
        player.currentTime = 0
      }
      this.videoOpen = false
      document.body.style.overflow = ''
    },
    stepVideo (delta) {
      const total = this.videoItems.length
      if (!total) return
      this.videoIndex = (this.videoIndex + delta + total) % total
      this.$nextTick(() => {
        const player = this.$refs.modalVideo
        if (player) {
          player.load()
          player.play().catch(() => {})
        }
      })
    },
    onKeydown (event) {
      if (!this.videoOpen) return
      if (event.key === 'Escape') this.closeVideo()
      if (event.key === 'ArrowLeft') this.stepVideo(-1)
      if (event.key === 'ArrowRight') this.stepVideo(1)
    }
  }
}
</script>

<style>
.knowledge-page {
  color: #171411;
  background: #f6f1e9;
}

.knowledge-hero {
  position: relative;
  min-height: clamp(320px, 42vh, 480px);
  overflow: hidden;
}

.knowledge-hero__media,
.knowledge-hero__overlay {
  position: absolute;
  inset: 0;
}

.knowledge-hero__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.knowledge-hero__overlay {
  background:
    linear-gradient(90deg, rgba(0, 0, 0, 0.72) 0%, rgba(0, 0, 0, 0.35) 55%, rgba(0, 0, 0, 0.2) 100%),
    linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.55) 100%);
}

.knowledge-hero__content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: clamp(320px, 42vh, 480px);
  padding: 72px 0 56px;
  color: #f5f3ef;
}

.knowledge-hero__eyebrow {
  margin: 0 0 16px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(245, 243, 239, 0.68);
}

.knowledge-hero__content h1 {
  margin: 0;
  max-width: 720px;
  font-size: clamp(34px, 4vw, 56px);
  font-weight: 400;
  line-height: 1.06;
}

.knowledge-news {
  background: #171411;
  color: #f5f3ef;
}

.knowledge-news__wrap {
  width: min(1600px, 100%);
  margin: 0 auto;
  padding: clamp(56px, 7vw, 100px) clamp(24px, 2.2vw, 35px);
}

.knowledge-news__title {
  margin: 0 0 clamp(32px, 4vw, 48px);
  font-size: clamp(28px, 3vw, 40px);
  font-weight: 400;
  letter-spacing: 0.03em;
}

.knowledge-news__list {
  display: flex;
  flex-direction: column;
}

.knowledge-news__item {
  display: flex;
  align-items: center;
  gap: 30px;
  padding: 30px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
  color: inherit;
  text-decoration: none;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.knowledge-news__item:hover {
  opacity: 0.92;
  transform: translateY(-2px);
}

.knowledge-news__item:hover .knowledge-news__image img {
  transform: scale(1.06);
}

.knowledge-news__image {
  flex: 0 0 400px;
  max-width: 42%;
  height: 240px;
  overflow: hidden;
  background: #2a2622;
  border-radius: 12px;
}

.knowledge-news__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.25s ease;
}

.knowledge-news__body {
  flex: 1;
  min-width: 0;
}

.knowledge-news__body h4 {
  margin: 0 0 10px;
  font-size: clamp(22px, 2.2vw, 32px);
  font-weight: 400;
  line-height: 1.22;
}

.knowledge-news__date {
  margin: 0 0 18px;
  font-size: 15px;
  color: rgba(245, 243, 239, 0.6);
}

.knowledge-news__excerpt {
  margin: 0;
  font-size: 16px;
  line-height: 1.65;
  color: rgba(245, 243, 239, 0.82);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.knowledge-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 48px;
}

.knowledge-pagination__btn {
  min-width: 44px;
  height: 44px;
  padding: 0 12px;
  border: 1px solid rgba(245, 243, 239, 0.28);
  border-radius: 8px;
  background: transparent;
  color: #f5f3ef;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.knowledge-pagination__btn:hover:not(:disabled) {
  border-color: #f5f3ef;
  background: rgba(245, 243, 239, 0.1);
}

.knowledge-pagination__btn.is-active {
  background: #f5f3ef;
  color: #171411;
  border-color: #f5f3ef;
}

.knowledge-pagination__btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.knowledge-videos {
  background: #f6f1e9;
}

.knowledge-videos__wrap {
  width: min(1600px, 100%);
  margin: 0 auto;
  padding: clamp(48px, 6vw, 100px) clamp(24px, 2.2vw, 35px);
}

.knowledge-videos__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 382px)) minmax(280px, 788px);
  grid-template-rows: repeat(2, minmax(180px, 240px));
  gap: 14px;
  justify-content: start;
}

.knowledge-videos__item {
  position: relative;
  padding: 0;
  border: 0;
  overflow: hidden;
  background: #2a2622;
  border-radius: 12px;
  cursor: pointer;
}

.knowledge-videos__item:nth-child(1) {
  grid-area: 1 / 1;
}

.knowledge-videos__item:nth-child(2) {
  grid-area: 1 / 2;
}

.knowledge-videos__item:nth-child(3) {
  grid-area: 2 / 1;
}

.knowledge-videos__item:nth-child(4) {
  grid-area: 2 / 2;
}

.knowledge-videos__item--featured {
  grid-column: 3;
  grid-row: 1 / span 2;
}

.knowledge-videos__item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.knowledge-videos__item:hover img {
  transform: scale(1.05);
  opacity: 0.92;
}

.knowledge-videos__play {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 56px;
  height: 56px;
  border: 2px solid rgba(255, 255, 255, 0.92);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.35);
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.2);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.knowledge-videos__play::after {
  position: absolute;
  top: 50%;
  left: 54%;
  border-top: 9px solid transparent;
  border-bottom: 9px solid transparent;
  border-left: 14px solid #fff;
  content: "";
  transform: translate(-50%, -50%);
}

.knowledge-videos__item--featured .knowledge-videos__play {
  width: 72px;
  height: 72px;
}

.knowledge-video-modal {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: grid;
  place-items: center;
  padding: 48px 88px;
  background: rgba(0, 0, 0, 0.9);
}

.knowledge-video-modal__player {
  width: min(1200px, 100%);
  max-height: 82vh;
  background: #000;
  border-radius: 12px;
  box-shadow: 0 28px 56px rgba(0, 0, 0, 0.28);
}

.knowledge-video-modal__close {
  position: absolute;
  top: 24px;
  right: 28px;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 42px;
  line-height: 1;
  cursor: pointer;
}

.knowledge-video-modal__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 28px;
  cursor: pointer;
}

.knowledge-video-modal__nav--prev {
  left: 24px;
}

.knowledge-video-modal__nav--next {
  right: 24px;
}

@media (max-width: 1024px) {
  .knowledge-news__list {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 20px;
  }

  .knowledge-news__item {
    flex-direction: column;
    align-items: stretch;
    width: calc(50% - 10px);
    padding-top: 0;
    padding-bottom: 20px;
  }

  .knowledge-news__image {
    flex: none;
    max-width: none;
    width: 100%;
    height: auto;
    aspect-ratio: 5 / 3;
  }

  .knowledge-news__body h4 {
    font-size: 24px;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    min-height: 58px;
  }

  .knowledge-news__date {
    margin-bottom: 12px;
    font-size: 12px;
  }

  .knowledge-videos__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: auto;
  }

  .knowledge-videos__item,
  .knowledge-videos__item--featured {
    grid-area: auto;
    min-height: 200px;
  }

  .knowledge-videos__item--featured {
    grid-column: 1 / -1;
    min-height: 280px;
  }
}

@media (max-width: 767px) {
  .knowledge-news__item {
    width: 100%;
  }

  .knowledge-news__body h4 {
    min-height: auto;
    font-size: 20px;
  }

  .knowledge-videos__grid {
    grid-template-columns: 1fr;
  }

  .knowledge-videos__item {
    min-height: 220px;
  }

  .knowledge-videos__item--featured {
    min-height: 260px;
  }

  .knowledge-video-modal {
    padding: 48px 20px;
  }
}
</style>
